import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ApolloProvider } from "/vendor/react-apollo";
import { Switch, withRouter, useLocation } from "/vendor/react-router-dom";

import compose from "/lib/util/compose";

import {
  AppThemeProvider,
  ResetStyles,
  ThemeStyles,
} from "/lib/component/base";

import {
  AuthenticatedRoute,
  UnauthenticatedRoute,
  AuthInvalidRoute,
  LastNamespaceRedirect,
  SigninRedirect,
  KeybindProvider,
  useIdentity,
  useApolloClient,
} from "/lib/component/util";

import { RelocationProvider } from "/lib/component/relocation";

import { SignInView } from "/lib/component/view";

import { AuthInvalidDialog, GlobalAlert } from "/lib/component/partial";
import jws from "jws-jwk/src/shims";
import invalidateTokens from "/lib/mutation/invalidateTokens";
import axios from "axios";

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [jwk, setJwk] = useState(null);
  const location = useLocation();
  const identity = useIdentity();
  const client = useApolloClient();

  const jwkEndpoint = "https://www.googleapis.com/oauth2/v2/certs";

  useEffect(() => {
    // Fetch the JWK object from the endpoint
    axios
      .get(jwkEndpoint)
      .then(response => {
        setJwk(response.data);
      })
      .catch(error => {
        console.error("Failed to fetch JWK object:", error);
      });
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");
    if (!token) {
      return;
    }
    if (jwk !== null) {
      const verified = jws.verify(token, jwk);
      if (verified) {
        const givenName = queryParams.get("givenName");
        const familyName = queryParams.get("familyName");
        const email = queryParams.get("email");
        if (email !== "") {
          localStorage.setItem("googleUser", email);
        }
        if (identity.sub !== `${givenName}${familyName}`.toLocaleLowerCase()) {
          setUser({
            givenName,
            familyName,
          });
        }
      } else {
        alert("Account not valid, sign in with a betmakers account");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, jwk]);

  useEffect(() => {
    if (user !== null) {
      invalidateTokens(client);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Render null until the JWK object is fetched
  if (jwk === null) {
    return null;
  }

  return (
    <KeybindProvider>
      <AppThemeProvider>
        <Switch>
          <UnauthenticatedRoute
            exact
            path="/signin"
            component={SignInView}
            fallbackComponent={LastNamespaceRedirect}
          />

          <AuthenticatedRoute
            render={() => children}
            fallbackComponent={SigninRedirect}
          />
        </Switch>
        <Switch>
          <UnauthenticatedRoute exact path="/signin" />
          <AuthInvalidRoute component={AuthInvalidDialog} />
        </Switch>
        <ResetStyles />
        <ThemeStyles />
        <GlobalAlert />
      </AppThemeProvider>
    </KeybindProvider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const AppRoot = ({ apolloClient, children }) => {
  return (
    <RelocationProvider>
      <ApolloProvider client={apolloClient}>
        <AuthProvider>{children}</AuthProvider>
      </ApolloProvider>
    </RelocationProvider>
  );
};

AppRoot.propTypes = {
  children: PropTypes.node.isRequired,
  apolloClient: PropTypes.object.isRequired,
};

export default compose(withRouter)(AppRoot);
